<div class="page">
  <!-- ===================== HERO HEADER ===================== -->
  <div class="detail-hero">
    <div class="dh-left">
      <p-avatar [label]="initials(partner()?.partnerName)" shape="circle" size="xlarge"
        styleClass="dh-avatar brand" />
      <div class="dh-meta">
        <div class="dh-title-row">
          <h2>{{ partner()?.partnerName || (loading() ? 'Loading…' : 'Partner') }}</h2>
          <p-tag [value]="mode()" [severity]="mode() === 'LIVE' ? 'success' : 'warn'"
            [icon]="mode() === 'LIVE' ? 'pi pi-bolt' : 'pi pi-wrench'" rounded />
          @if (partner(); as p) {
            <p-tag [value]="p.isActive ? 'Active' : 'Inactive'"
              [severity]="p.isActive ? 'success' : 'secondary'"
              [icon]="p.isActive ? 'pi pi-check-circle' : 'pi pi-ban'" rounded />
          }
        </div>
        <div class="dh-sub">
          @if (partner()?.partnerCode) {
            <span class="dh-code"><i class="pi pi-hashtag"></i> {{ partner()!.partnerCode }}</span>
          }
        </div>
        @if (partner(); as p) {
          <div class="dh-chips">
            @if (p.contactPerson) { <p-chip [label]="p.contactPerson" icon="pi pi-user" /> }
            @if (p.email) { <p-chip [label]="p.email" icon="pi pi-envelope" /> }
            @if (p.phone) { <p-chip [label]="p.phone" icon="pi pi-phone" /> }
            @if (p.city || p.state) {
              <p-chip [label]="(p.city || '') + (p.state ? (p.city ? ', ' : '') + p.state : '')"
                icon="pi pi-map-marker" />
            }
          </div>
        }
      </div>
    </div>
    <div class="head-actions">
      <p-button label="Refresh" icon="pi pi-refresh" [outlined]="true" severity="secondary"
        size="small" (onClick)="load()" />
      <p-button label="Back" icon="pi pi-arrow-left" [text]="true" (onClick)="back()" />
    </div>
  </div>

  <!-- ===================== TABS ===================== -->
  <div class="card tabs-card">
    <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)">
      <p-tablist>
        <p-tab value="gateway"><i class="pi pi-credit-card"></i> Payment Gateway</p-tab>
        <p-tab value="products"><i class="pi pi-box"></i> Products</p-tab>
        <p-tab value="messaging"><i class="pi pi-comments"></i> Messaging</p-tab>
        <p-tab value="mode"><i class="pi pi-bolt"></i> Live &amp; Test</p-tab>
      </p-tablist>

      <p-tabpanels>
        <!-- ---------- payment gateway ---------- -->
        <p-tabpanel value="gateway">
          <div class="pane">
            <h3>Payment Gateway</h3>
            <p class="lede">
              Which integration this partner transacts through. One configuration per partner.
            </p>

            @if (gateway(); as g) {
              <div class="now" [class.warn]="!g.effectiveLabel">
                <span class="now-lbl">Currently using</span>
                @if (g.effectiveLabel) {
                  <strong>{{ g.effectiveLabel }}</strong>
                  <span class="now-src">{{ g.effectiveSource }}</span>
                } @else {
                  <strong class="bad">No gateway for {{ g.mode }} mode</strong>
                  <span class="now-src">Payments will fail until one is available.</span>
                }
              </div>
            }

            <label class="fld-label">Payment Integration</label>
            <p-select [options]="options()" optionLabel="label" optionValue="value"
              [ngModel]="pick()" (ngModelChange)="pick.set($event)" [showClear]="true"
              placeholder="Use the global gateway for this mode" appendTo="body"
              styleClass="w-full" />
            <p class="hint">
              Leaving this unset is the normal arrangement: the partner uses the GLOBAL integration
              matching its own mode, so one live row and one test row serve every partner. Assign one
              only when this partner must transact through a particular integration — a second
              merchant account, say. An assignment in the wrong mode is ignored rather than used.
            </p>

            <div class="acts">
              <p-button label="Save" icon="pi pi-check" [loading]="savingGw()" (onClick)="saveGateway()" />
            </div>
          </div>
        </p-tabpanel>

        <!-- ---------- products ---------- -->
        <p-tabpanel value="products">
          <div class="pane">
            <h3>Products</h3>
            <p class="lede">
              The premium plans this partner is allowed to sell. Only this partner's mappings are
              listed here.
            </p>

            @if (productsLoading()) {
              <div class="state"><i class="pi pi-spin pi-spinner"></i> Loading…</div>
            } @else {
              @if (assigned().length) {
                <table class="prod">
                  <thead>
                    <tr><th>Plan</th><th>Master policy</th><th class="c">Status</th><th class="r"></th></tr>
                  </thead>
                  <tbody>
                    @for (a of assigned(); track a.id) {
                      <tr>
                        <td class="nm">{{ a.premiumCode || a.coverageType || 'Plan #' + (a.premiumID ?? a.premiumId) }}</td>
                        <td>{{ a.masterPolicyName || '—' }}</td>
                        <td class="c">
                          <p-tag [value]="a.isEnable ? 'Enabled' : 'Disabled'"
                            [severity]="a.isEnable ? 'success' : 'secondary'" />
                        </td>
                        <td class="r">
                          <p-button label="Remove" icon="pi pi-times" [text]="true" severity="danger"
                            size="small" [loading]="savingProduct()" (onClick)="removeProduct(a)" />
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              } @else {
                <div class="none-box">
                  No plans assigned yet — this partner cannot sell anything until at least one is added.
                </div>
              }

              <label class="fld-label add-lbl">Add a plan</label>
              <div class="add-row">
                <p-select [options]="planOptions()" optionLabel="label" optionValue="value"
                  [ngModel]="addPick()" (ngModelChange)="addPick.set($event)"
                  placeholder="Select a premium plan" appendTo="body" styleClass="w-full plan-select" />
                <p-button label="Add" icon="pi pi-plus" [disabled]="!addPick()"
                  [loading]="savingProduct()" (onClick)="addProduct()" />
              </div>
              @if (!allRules().length) {
                <p class="hint">
                  No plans are available to this partner. Plans under a master policy owned by a
                  CLIENT belong to that client and cannot be sold by a partner — create a
                  partner-owned master policy, or one with no owner, to make plans assignable here.
                </p>
              } @else if (!available().length) {
                <p class="hint">Every available plan is already assigned to this partner.</p>
              }
            }
          </div>
        </p-tabpanel>

        <!-- ---------- messaging ---------- -->
        <p-tabpanel value="messaging">
          <div class="pane">
            <h3>Messaging</h3>
            <p class="lede">
              Which channels each message goes out on for this partner. Rows left on
              <b>Inherit</b> follow the global defaults. Login OTP always sends, so a partner
              can never be locked out.
            </p>
            @if (partnerId()) {
              <app-messaging-rules lockedScopeType="PARTNER" [lockedScopeId]="partnerId()!" />
            }
          </div>
        </p-tabpanel>

        <!-- ---------- live / test ---------- -->
        <p-tabpanel value="mode">
          <div class="pane">
            <h3>Live &amp; Test Mode</h3>
            <p class="lede">
              A partner starts in test. Going live needs every check below to pass; going back to
              test never does.
            </p>

            @if (checking()) {
              <div class="state"><i class="pi pi-spin pi-spinner"></i> Checking…</div>
            } @else if (status(); as s) {
              <div class="cur">
                Currently <p-tag [value]="s.mode" [severity]="s.mode === 'LIVE' ? 'success' : 'warn'" />
              </div>

              <ul class="checks">
                @for (c of s.checks; track c.key) {
                  <li [class.ok]="c.passed">
                    <i class="pi" [class.pi-check-circle]="c.passed" [class.pi-times-circle]="!c.passed"></i>
                    <div>
                      <strong>{{ c.label }}</strong>
                      <span>{{ c.detail }}</span>
                      @if (!c.passed) { <em>{{ c.fixHint }}</em> }
                    </div>
                  </li>
                }
              </ul>

              @if (s.canGoLive) {
                <div class="verdict ok"><i class="pi pi-check-circle"></i> All checks passed — this partner can go live.</div>
              } @else {
                <div class="verdict no">
                  <i class="pi pi-lock"></i>
                  Stays in test until every check passes. Enrolments made now are test data.
                </div>
              }

              <div class="acts">
                @if (s.mode === 'TEST') {
                  <p-button label="Go Live" icon="pi pi-bolt" severity="success"
                    [disabled]="!s.canGoLive" [loading]="savingMode()" (onClick)="setMode('LIVE')" />
                } @else {
                  <p-button label="Back to Test" icon="pi pi-undo" severity="secondary"
                    [loading]="savingMode()" (onClick)="setMode('TEST')" />
                }
              </div>
            } @else {
              <div class="state">Could not load the readiness checks.</div>
            }
          </div>
        </p-tabpanel>
      </p-tabpanels>
    </p-tabs>
  </div>
</div>
